<script setup>
import { computed, ref } from 'vue'
import DateInput from '@/components/DateInput.vue'
import { salesInformationGetSalesInfo } from '@/api/cfes/salesInformation'
import { toast } from '@/utils/toast'
import { buildSalesTrend, buildTrendScale, normalizeSalesStatus } from '@/utils/salesTrend'

const startDate = ref('')
const endDate = ref('')
const loading = ref(false)
const visible = ref(false)

const salesInformationHeaders = [
  { title: 'Sop Id', key: 'sopId' },
  { title: 'Article Id', key: 'articleId' },
  { title: 'State', key: 'status' },
  { title: 'Amount', key: 'amount' },
  { title: 'Calculation Date', key: 'calcDate' },
]
const salesInformationSummarizedHeaders = [
  { title: 'State', key: 'status' },
  { title: 'Amount', key: 'amount' },
]

const salesInformationSet = ref([])
const salesInformationSummarizedSet = ref([])
const trend = ref(buildSalesTrend())
const hiddenStatuses = ref([])
const hoveredStatus = ref(null)
const focusedStatus = ref(null)
const highlightedStatus = computed(() => hoveredStatus.value ?? focusedStatus.value)
const chartSeries = computed(() => trend.value.series.filter((series) => !hiddenStatuses.value.includes(series.name)))
const renderedSeries = computed(() => [
  ...chartSeries.value.filter((series) => series.name !== highlightedStatus.value),
  ...chartSeries.value.filter((series) => series.name === highlightedStatus.value),
])
const undatedStatuses = computed(() => [...new Set(salesInformationSet.value
  .filter((row) => !String(row.calcDate ?? ''))
  .map((row) => normalizeSalesStatus(row.status)))].sort())
const chartScale = computed(() => buildTrendScale(chartSeries.value))
const chartWidth = computed(() => Math.max(760, trend.value.dates.length * 72 + 88))
const chartX = (index) => {
  const width = chartWidth.value - 88
  return trend.value.dates.length === 1 ? 64 + width / 2 : 64 + index * width / Math.max(1, trend.value.dates.length - 1)
}
const chartY = (value) => 244 - (value - chartScale.value.min) / (chartScale.value.max - chartScale.value.min) * 220
const chartPoints = (series) => series.values.map((value, index) => `${chartX(index)},${chartY(value)}`).join(' ')
const formatAmount = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)
const formatTick = (value) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value)
const shortDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.slice(5).replace('-', '/') : value
const toggleStatus = (name) => {
  hiddenStatuses.value = hiddenStatuses.value.includes(name)
    ? hiddenStatuses.value.filter((status) => status !== name)
    : [...hiddenStatuses.value, name]
}
const dayDescription = (index) => `${trend.value.dates[index]}: ${chartSeries.value.map((series) => `${series.name} ${formatAmount(series.values[index])}`).join(', ')}`

const formatDate = (yyyymmdd) => {
  if (!yyyymmdd || yyyymmdd.length !== 8) return yyyymmdd
  const y = yyyymmdd.slice(0, 4)
  const m = yyyymmdd.slice(4, 6)
  const d = yyyymmdd.slice(6, 8)
  return `${m}/${d}/${y}`
}

const search = async () => {
  if (!startDate.value || !endDate.value) {
    toast.warning('Please select start and end date')
    return
  }

  loading.value = true
  try {
    const res = await salesInformationGetSalesInfo(
      startDate.value.replaceAll('-', ''),
      endDate.value.replaceAll('-', '')
    )
    const rows = Array.isArray(res) ? res : res?.data || []
    const normalized = rows.map((i) => ({
      ...i,
      status: normalizeSalesStatus(i.status),
      calcDate: formatDate(String(i.calcDate ?? '')),
      amount: Number.isFinite(Number(i.amount)) ? Number(i.amount) : 0,
    }))
    salesInformationSet.value = normalized
    summarize(normalized)
    buildChart(normalized)
    visible.value = true
  } catch (err) {
    console.error('search sales info failed', err)
    toast.error('Failed to load sales info')
  } finally {
    loading.value = false
  }
}

const summarize = (rows) => {
  const map = new Map()
  rows.forEach((r) => {
    const key = r.status
    map.set(key, (map.get(key) || 0) + r.amount)
  })
  salesInformationSummarizedSet.value = Array.from(map.entries()).map(([status, amount]) => ({ status, amount }))
}

const buildChart = (rows) => {
  trend.value = buildSalesTrend(rows)
  hiddenStatuses.value = []
  hoveredStatus.value = null
  focusedStatus.value = null
}

const clear = () => {
  startDate.value = ''
  endDate.value = ''
  visible.value = false
  salesInformationSet.value = []
  salesInformationSummarizedSet.value = []
  trend.value = buildSalesTrend()
  hiddenStatuses.value = []
  hoveredStatus.value = null
  focusedStatus.value = null
}
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">Sales Information</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-card class="section-card mb-4">
      <v-card-title>Filters</v-card-title>
      <v-card-text>
        <v-row>
           <v-col cols="12" md="3">
              <DateInput v-model="startDate" label="Start Date" />
            </v-col>
            <v-col cols="12" md="3">
              <DateInput v-model="endDate" label="End Date" />
            </v-col>
           <v-col cols="12" md="3" class="d-flex align-center">
              <v-btn color="primary" class="mr-2" @click="search">Search</v-btn>
              <v-btn variant="tonal" color="primary" @click="clear">Clear</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

    <template v-if="loading">
      <div class="loader-container">
        <v-progress-circular indeterminate :size="120" :width="8" color="primary">loading</v-progress-circular>
      </div>
    </template>

    <template v-if="visible">
      <v-card class="section-card mb-4">
        <v-card-title>Sales Sum Total</v-card-title>
        <v-data-table
          dense
          :headers="salesInformationSummarizedHeaders"
          :items="salesInformationSummarizedSet"
          class="elevation-1 monitoring-table"
          disable-sort
          disable-pagination
          hide-default-footer
        />
      </v-card>

      <v-card class="section-card trend-card mb-4">
        <div class="trend-heading">
          <div>
            <h2 class="trend-title"><v-icon icon="mdi-chart-line" size="22" color="primary" />Sales Trend</h2>
            <p class="trend-subtitle">Daily sales amount by state · hover or focus a date for details</p>
          </div>
          <span v-if="trend.dates.length" class="trend-range">{{ trend.dates[0] }} — {{ trend.dates[trend.dates.length - 1] }}</span>
        </div>
        <v-card-text>
          <v-alert v-if="undatedStatuses.length" type="warning" variant="tonal" density="compact" class="mb-4">
            {{ undatedStatuses.join(', ') }}: records without a calculation date cannot be plotted in the daily trend. They remain included in Sales Sum Total and Sales Detail.
          </v-alert>
          <div v-if="!trend.dates.length" class="trend-empty" role="status">
            <v-icon icon="mdi-chart-timeline-variant" size="44" color="grey-lighten-1" />
            <strong>No sales data available</strong>
            <span>Try another date range to view daily trends.</span>
          </div>
          <template v-else>
            <div class="trend-summary">
              <div class="trend-stat"><span>Total amount</span><strong>{{ formatAmount(trend.total) }}</strong><small>Across {{ trend.dates.length }} reported days</small></div>
              <div class="trend-stat"><span>Daily average</span><strong>{{ formatAmount(trend.average) }}</strong><small>Per reported day · all states</small></div>
              <div class="trend-stat"><span>Peak daily amount</span><strong>{{ formatAmount(trend.peak) }}</strong><small>{{ trend.peakDate }} · all states</small></div>
            </div>
            <div class="trend-legend" aria-label="Toggle sales states">
              <button v-for="series in trend.series" :key="series.name" type="button" class="trend-legend-item"
                :class="{ 'is-muted': hiddenStatuses.includes(series.name), 'is-highlighted': highlightedStatus === series.name }"
                :aria-pressed="!hiddenStatuses.includes(series.name)" @click="toggleStatus(series.name)"
                @mouseenter="hoveredStatus = series.name" @mouseleave="hoveredStatus = null"
                @focus="focusedStatus = series.name" @blur="focusedStatus = null">
                <svg width="28" height="12" aria-hidden="true"><line x1="0" y1="6" x2="28" y2="6" :stroke="series.color" :stroke-dasharray="series.dashArray" stroke-width="3" /></svg>{{ series.name }}
              </button>
              <span class="trend-legend-hint">Hover / focus to highlight · click to show / hide</span>
            </div>
            <div v-if="!chartSeries.length" class="trend-empty" role="status">
              <span>Select a state above to display its trend.</span>
            </div>
            <div v-else class="trend-scroll" tabindex="0" role="region" aria-label="Daily sales trend chart, scroll horizontally for more dates">
              <svg class="trend-chart" :viewBox="`0 0 ${chartWidth} 300`" :style="{ minWidth: `${chartWidth}px` }" aria-label="Sales amount by date">
                <g v-for="tick in chartScale.ticks" :key="tick">
                  <line x1="64" :x2="chartWidth - 24" :y1="chartY(tick)" :y2="chartY(tick)" class="trend-grid" />
                  <text x="52" :y="chartY(tick) + 4" text-anchor="end" class="trend-axis">{{ formatTick(tick) }}</text>
                </g>
                <text x="64" y="13" class="trend-axis">Amount</text>
                <polyline v-for="series in renderedSeries" :key="series.name" :points="chartPoints(series)"
                  fill="none" :stroke="series.color" :stroke-dasharray="series.dashArray"
                  :stroke-width="highlightedStatus === series.name ? 5 : 3"
                  :opacity="highlightedStatus && highlightedStatus !== series.name ? 0.25 : 1"
                  stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
                <g v-for="(date, index) in trend.dates" :key="date" class="trend-day">
                  <text :x="chartX(index)" y="272" text-anchor="middle" class="trend-axis">{{ shortDate(date) }}</text>
                  <line :x1="chartX(index)" :x2="chartX(index)" y1="24" y2="244" class="trend-guide" />
                  <circle v-for="series in renderedSeries" :key="series.name" :cx="chartX(index)" :cy="chartY(series.values[index])"
                    :r="highlightedStatus === series.name ? 6 : 4" :fill="series.color"
                    :opacity="highlightedStatus && highlightedStatus !== series.name ? 0.25 : 1" stroke="white" stroke-width="2" />
                  <v-tooltip location="top" content-class="trend-tooltip">
                    <template #activator="{ props }">
                      <rect v-bind="props" :x="chartX(index) - 30" y="20" width="60" height="230" fill="transparent"
                        tabindex="0" role="img" :aria-label="dayDescription(index)" class="trend-hit-area" />
                    </template>
                    <div class="trend-tooltip-date">{{ date }}</div>
                    <div v-for="series in chartSeries" :key="series.name" class="trend-tooltip-row">
                      <span class="trend-dot" :style="{ backgroundColor: series.color }"></span>
                      <span>{{ series.name }}</span><strong>{{ formatAmount(series.values[index]) }}</strong>
                    </div>
                  </v-tooltip>
                </g>
              </svg>
            </div>
            <p class="trend-footnote">Dates with returned data only. States with equal amounts may overlap; hover or focus a legend state to bring it to the front, or hide other states. Summary includes all dated states; legend selection only changes the chart.</p>
          </template>
        </v-card-text>
      </v-card>
      <v-card class="section-card">
        <v-card-title>Sales Detail</v-card-title>
        <v-data-table
          density="compact"
          :headers="salesInformationHeaders"
          :items="salesInformationSet"
          :items-per-page="-1"
          class="elevation-1 monitoring-table"
          disable-sort
          hide-default-footer
        />
      </v-card>
    </template>

  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}

.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 0;
}

.monitoring-table :deep(.v-data-table__wrapper) {
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.monitoring-table :deep(tbody tr:nth-child(odd)) {
  background-color: #f9fafb;
}

.monitoring-table :deep(tbody tr:hover) {
  background-color: #eef2ff;
}

.trend-card { overflow: hidden; }
.trend-heading { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 14px 16px 4px; }
.trend-title { display: flex; align-items: center; gap: 10px; color: #0f172a; font-size: 1.1rem; font-weight: 700; }
.trend-subtitle { margin: 6px 0 0; color: #64748b; font-size: 12px; }
.trend-range { padding: 6px 10px; border-radius: 8px; background: #eff6ff; color: #2563eb; font-size: 12px; }
.trend-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 20px; }
.trend-stat { display: flex; flex-direction: column; gap: 6px; padding: 16px 18px; border: 1px solid #e2e8f0; border-radius: 12px; background: linear-gradient(135deg, #f8fafc, #eff6ff); }
.trend-stat > span { color: #64748b; font-size: 12px; font-weight: 600; }
.trend-stat strong { color: #0f172a; font-size: clamp(20px, 2.3vw, 28px); line-height: 1.2; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.trend-stat small { color: #64748b; font-size: 11px; }
.trend-legend { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.trend-legend-item { display: inline-flex; align-items: center; gap: 7px; padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 8px; background: white; color: #334155; font: inherit; font-size: 12px; cursor: pointer; overflow-wrap: anywhere; }
.trend-legend-item:hover { background: #f1f5f9; border-color: #94a3b8; }
.trend-legend-item.is-highlighted { background: #eff6ff; border-color: #2563eb; }
.trend-legend-item:focus-visible, .trend-scroll:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }
.trend-legend-item.is-muted { opacity: 0.5; text-decoration: line-through; }
.trend-dot { display: inline-block; flex-shrink: 0; width: 9px; height: 9px; border-radius: 50%; }
.trend-legend-hint { margin-left: auto; color: #64748b; font-size: 11px; }
.trend-scroll { width: 100%; overflow-x: auto; border: 1px solid #eef2f7; border-radius: 12px; background: linear-gradient(180deg, #f8fbff, #fff); }
.trend-chart { display: block; width: 100%; height: auto; }
.trend-grid { stroke: #e2e8f0; stroke-dasharray: 4 5; }
.trend-axis { fill: #64748b; font-size: 11px; }
.trend-guide { stroke: #94a3b8; stroke-dasharray: 4 4; opacity: 0; }
.trend-day:hover .trend-guide, .trend-day:focus-within .trend-guide { opacity: 1; }
.trend-hit-area { cursor: crosshair; }
.trend-hit-area:focus-visible { outline: none; stroke: #2563eb; stroke-width: 1; }
.trend-tooltip-date { font-size: 12px; font-weight: 700; margin-bottom: 8px; }
.trend-tooltip-row { display: flex; align-items: center; gap: 8px; font-size: 12px; padding: 3px 0; }
.trend-tooltip-row strong { margin-left: auto; padding-left: 16px; font-variant-numeric: tabular-nums; }
.trend-footnote { margin: 12px 0 0; color: #64748b; font-size: 11px; }
.trend-empty { min-height: 230px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 12px; color: #64748b; text-align: center; }
.trend-empty strong { color: #334155; }
@media (max-width: 600px) {
  .trend-summary { grid-template-columns: 1fr; gap: 8px; }
  .trend-stat { padding: 12px 14px; }
  .trend-heading { padding: 10px 16px 4px; }
  .trend-range { max-width: 100%; overflow-wrap: anywhere; }
  .trend-legend-hint { width: 100%; margin-left: 0; }
}
</style>

